import React, { useState } from 'react';
import { Box, Drawer, Button, Typography, List, ListItemText, ListItemButton, ListItemIcon } from '@mui/material';
import { useUser } from '../context/userContext';
import { logout } from '../api/axios';
import { Link } from 'react-router-dom';

// Import our custom navigation hook
import { useNavigation, AnimatedNavLink } from './PageTransitionWrapper';

import VideoChatIcon from '@mui/icons-material/VideoChat';
import {ReactComponent as OliIcon} from '../static/icons/Algo Olio.svg'
import {ReactComponent as HomeIcon} from '../static/icons/Home.svg'
import {ReactComponent as CourseIcon} from '../static/icons/Course.svg'
import {ReactComponent as ResumeIcon} from '../static/icons/Resume.svg'
import {ReactComponent as InterviewIcon} from '../static/icons/Interview.svg';
import {ReactComponent as SettingsIcon} from '../static/icons/Settings.svg'
import {ReactComponent as UpgradeStarIcon} from '../static/icons/Upgrade Star.svg'
import {ReactComponent as LogoutIcon} from '../static/icons/Log Out.svg'

// Define the navigation order for animation directions
const NAV_ORDER = [
  '/dashboard',
  '/courses',
  '/resume/upload',
  '/404NotFound',  // Virtual Interviewer route
  '/settings'
];

const SideNav = ({ isCollapsed, setIsCollapsed }) => {
    const [settingsOpen, setSettingsOpen] = useState(false);
    const { user, contextLogout } = useUser();
    const { currentPath } = useNavigation();

    const isPremium = user && user.data.planType === 'Premium';
    const isTestFree = !isPremium;

    const handleLogoutClick = () => {
        logout();
        contextLogout();
        window.location.href = '/login';
    };

    // Determine animation direction based on current and target path
    const getDirection = (targetPath) => {
        const currentIndex = NAV_ORDER.indexOf(currentPath);
        const targetIndex = NAV_ORDER.indexOf(targetPath);
        
        // If either path is not in our order list, default to 'down'
        if (currentIndex === -1 || targetIndex === -1) return 'down';
        
        return targetIndex > currentIndex ? 'up' : 'down';
    };

    return (
        <Drawer
          variant="permanent"
          sx={{
            width: '12%',
            flexShrink: 0,
            "& .MuiDrawer-paper": { 
                minWidth: '13%',
                boxSizing: "border-box",  
                background: 'linear-gradient(0deg, rgba(0, 0, 0, 0.25) 0%, rgba(0, 0, 0, 0.25) 100%), linear-gradient(326deg, #A29EDA -46.9%, #916CB4 -29.33%, #8E3A88 27.57%, #6B3A8F 73.58%, #5D2A79 115.7%)',
            },
          }}
        >
            <Box sx={{
                width: '100%',
                height: '30%',
                display: 'flex',
                flexDirection: 'column',
                color: '#E1E1E6',
                alignItems: 'center',
                justifyContent: 'center'
            }}>
                <OliIcon width='100' height='100'/>
                {!isPremium && (
                    <AnimatedNavLink 
                        to="/settings"
                        direction="down"
                        component={Button}
                        sx={{
                            width: '70%',
                            height: '20%',
                            display: 'flex', 
                            gap: 2, 
                            marginTop: '15%',
                            textTransform: 'none',
                            background: '#F3F3F31A',
                            border: '1px solid #FFFFFF73',
                            borderRadius: '24px',
                            boxShadow: '10px 10px 80px 0px #3F27661A',
                        }}
                    >
                        <UpgradeStarIcon />
                        <Typography sx={{color: '#E1E1E6'}}>Upgrade</Typography>
                    </AnimatedNavLink>
                )}
            </Box>

            <List sx={{height:'30%'}}>
                {/* Dashboard */}
                <AnimatedNavLink 
                  data-cy='dashboard-link'
                  to="/dashboard"
                  direction={getDirection('/dashboard')}
                  component={ListItemButton}
                  sx={{paddingLeft: '18%', paddingBottom: '12%', color:'white', fontSize: '16px'}}
                >
                    <ListItemIcon><HomeIcon color='#E1E1E6'/></ListItemIcon>
                    <ListItemText primary="Dashboard" />
                </AnimatedNavLink>
                
                {/* Courses */}
                <AnimatedNavLink 
                  data-cy='courses-link'
                  to="/courses"
                  direction={getDirection('/courses')}
                  component={ListItemButton}
                  sx={{paddingLeft: '18%', paddingBottom: '6%', color:'white', fontSize: '16px'}}
                >
                    <ListItemIcon><CourseIcon color='#E1E1E6'/></ListItemIcon>
                    <ListItemText primary="Courses" />
                </AnimatedNavLink>
                
                {isPremium && (
                <>
                {/* Resume Checker */}
                <AnimatedNavLink 
                  data-cy='resume-checker-link'
                  to="/resume/upload"
                  direction={getDirection('/resume/upload')}
                  component={ListItemButton}
                  sx={{paddingLeft: '18%', paddingBottom: '4%', color:'white', fontSize: '16px'}}
                >
                    <ListItemIcon><ResumeIcon color='#E1E1E6'/></ListItemIcon>
                    <ListItemText primary="Resume Checker" />
                </AnimatedNavLink>
                
                {/* Virtual Interviewer */}
                <AnimatedNavLink 
                  data-cy='virtual-interviewer-link'
                  to="/interview-start-page"
                  direction={getDirection('/404NotFound')}
                  component={ListItemButton}
                  sx={{paddingLeft: '18%', paddingBottom: '6%', color:'white', fontSize: '16px'}}
                >
                    <ListItemIcon><InterviewIcon color='#E1E1E6'/></ListItemIcon>
                    <ListItemText primary="Virtual Interviewer" />
                </AnimatedNavLink>
                </>
                )}
            </List>

            <Box sx={{ flexGrow: 1 }} />

            <List>
                {/* Settings */}
                <AnimatedNavLink 
                  data-cy='settings-link'
                  to="/settings"
                  direction={getDirection('/settings')}
                  component={ListItemButton}
                  sx={{paddingLeft: '18%', paddingBottom:'10%', marginTop: '90%', color:'white', fontSize: '16px'}}
                >
                    <ListItemIcon><SettingsIcon /></ListItemIcon>
                    <ListItemText> Settings </ListItemText>
                </AnimatedNavLink>
                
                {/* Logout */}
                <ListItemButton 
                  data-cy='logout-link'
                  onClick={handleLogoutClick} 
                  sx={{paddingLeft: '18%', color:'white', fontSize: '16px'}}
                >
                    <ListItemIcon><LogoutIcon /></ListItemIcon>
                    <ListItemText> Log Out </ListItemText>
                </ListItemButton>
            </List>
        </Drawer>
    );
};

export default SideNav;